import { jsx as _jsx } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { useManagedModalLifecycleContext } from '#pplx/frontend/aether/components/Modal/context/ManagedModalLifecycleContext.js';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
const ManagedModalRecoveryOwnerContext = createContext(null);
const renderNullFallback = () => null;
/**
 * Contains all errors escaping a public Modal subtree, including its exit hook
 * and portal commits. Reports through the resolved Aether logger (inherited or
 * explicitly injected; no-op without either), renders nothing, and permits reopening
 * when controlled state changes.
 *
 * Only the first boundary consuming a managed lifecycle owns its recovery.
 * Descendant standalone modals close their own owner; recovery ownership stays
 * separate from the shared exit lifecycle so normal nested animations are unchanged.
 */
export function ModalErrorBoundary({ children, isOpen = true, onClose, variant, }) {
    const { logger } = useAetherContext();
    const manager = useManagedModalLifecycleContext();
    const parentRecoveryOwner = useContext(ManagedModalRecoveryOwnerContext);
    const recoveryOwner = manager !== parentRecoveryOwner ? manager : null;
    const resetKeys = useMemo(() => [isOpen], [isOpen]);
    const reportError = useCallback((error, errorInfo) => {
        logger.error('AETHER_MODAL_BOUNDARY_ERROR', {
            error,
            modalBoundary: true,
            modalVariant: variant,
            componentStack: errorInfo.componentStack,
            errorName: error.name,
            errorMessage: error.message,
        }, error);
        if (recoveryOwner) {
            recoveryOwner.onErrorRecovery();
        }
        else if (isOpen) {
            onClose();
        }
    }, [isOpen, logger, onClose, recoveryOwner, variant]);
    return (_jsx(ErrorBoundary, { fallbackRender: renderNullFallback, onError: reportError, resetKeys: resetKeys, children: _jsx(ManagedModalRecoveryOwnerContext.Provider, { value: recoveryOwner ?? parentRecoveryOwner, children: children }) }));
}
//# sourceMappingURL=ModalErrorBoundary.js.map